let transitionStyle: HTMLStyleElement | null = null
let transitionTimeout: ReturnType<typeof setTimeout> | null = null
let cancelPendingThemeFlip: (() => void) | null = null
let restorePatchedClassList: (() => void) | null = null

const THEME_CLASS = 'light'
const THEME_TRANSITION_CLEANUP_DELAY_MS = 260
const THEME_TRANSITION_CSS =
	':root { --button-theme-bg-transition-duration: 0.2s; --button-theme-chrome-transition-duration: 0.2s; } *, *::before, *::after { transition: all 0.2s ease !important; }'

function onNextFrame(callback: () => void) {
	if (typeof requestAnimationFrame === 'function') {
		const handle = requestAnimationFrame(callback)
		return () => cancelAnimationFrame(handle)
	}

	const handle = setTimeout(callback, 0)
	return () => clearTimeout(handle)
}

function clearPendingThemeFlip() {
	cancelPendingThemeFlip?.()
	cancelPendingThemeFlip = null
}

function getTransitionStyle(doc?: Document) {
	if (doc && transitionStyle && transitionStyle.ownerDocument !== doc) {
		transitionStyle = null
	}
	if (transitionStyle) return transitionStyle
	if (doc == null) return null
	transitionStyle = doc.createElement('style')
	doc.head.appendChild(transitionStyle)
	return transitionStyle
}

function clearThemeTransition() {
	if (transitionTimeout) {
		clearTimeout(transitionTimeout)
		transitionTimeout = null
	}
	clearPendingThemeFlip()
	if (transitionStyle) transitionStyle.textContent = ''
}

function stageThemeFlip(doc: Document, applyThemeChange: () => void) {
	clearThemeTransition()
	const style = getTransitionStyle(doc)
	if (style == null) {
		applyThemeChange()
		return
	}

	style.textContent = THEME_TRANSITION_CSS
	void doc.documentElement.offsetHeight

	cancelPendingThemeFlip = onNextFrame(() => {
		cancelPendingThemeFlip = null
		applyThemeChange()
		transitionTimeout = setTimeout(() => {
			clearThemeTransition()
		}, THEME_TRANSITION_CLEANUP_DELAY_MS)
	})
}

export function initThemeTransition(doc?: Document) {
	const resolvedDocument =
		doc ?? (typeof document !== 'undefined' ? document : undefined)
	if (resolvedDocument == null) return () => {}
	const classList: DOMTokenList & {
		add: (...tokens: string[]) => void
		remove: (...tokens: string[]) => void
		toggle: (token: string, force?: boolean) => boolean
		replace: (oldToken: string, newToken: string) => boolean
	} = resolvedDocument.documentElement.classList

	if (restorePatchedClassList) {
		restorePatchedClassList()
	}

	const originalAdd = classList.add.bind(classList)
	const originalRemove = classList.remove.bind(classList)
	const originalToggle = classList.toggle.bind(classList)
	const originalReplace = classList.replace.bind(classList)

	const shouldIntercept = (tokens: string[]) => tokens.includes(THEME_CLASS)

	classList.add = (...tokens: string[]) => {
		if (!shouldIntercept(tokens) || classList.contains(THEME_CLASS)) {
			return originalAdd(...tokens)
		}
		stageThemeFlip(resolvedDocument, () => {
			originalAdd(...tokens)
		})
	}

	classList.remove = (...tokens: string[]) => {
		if (!shouldIntercept(tokens) || !classList.contains(THEME_CLASS)) {
			return originalRemove(...tokens)
		}
		stageThemeFlip(resolvedDocument, () => {
			originalRemove(...tokens)
		})
	}

	classList.toggle = (token: string, force?: boolean) => {
		if (token !== THEME_CLASS) {
			return originalToggle(token, force)
		}

		const currentlyEnabled = classList.contains(token)
		const nextEnabled = force == null ? !currentlyEnabled : Boolean(force)

		if (nextEnabled === currentlyEnabled) {
			return originalToggle(token, force)
		}

		stageThemeFlip(resolvedDocument, () => {
			originalToggle(token, force)
		})
		return nextEnabled
	}

	classList.replace = (oldToken: string, newToken: string) => {
		if (oldToken !== THEME_CLASS && newToken !== THEME_CLASS) {
			return originalReplace(oldToken, newToken)
		}

		const hasOldToken = classList.contains(oldToken)
		if (!hasOldToken && oldToken !== newToken) {
			return originalReplace(oldToken, newToken)
		}

		stageThemeFlip(resolvedDocument, () => {
			originalReplace(oldToken, newToken)
		})
		return hasOldToken
	}

	restorePatchedClassList = () => {
		classList.add = originalAdd
		classList.remove = originalRemove
		classList.toggle = originalToggle
		classList.replace = originalReplace
		restorePatchedClassList = null
	}

	return () => {
		restorePatchedClassList?.()
		clearThemeTransition()
	}
}
